/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useRef, memo } from 'react';
import Plyr from 'plyr';
import { Options } from 'plyr';
import Hls from 'hls.js';
import { useSession } from 'next-auth/client';
import { sendVideoWatchDepth } from '@/utils/tracker';
import { useRouter } from 'next/router';
import { convertLanguageCodeToName } from '@/utils/index';

type PlyrProps = {
  poster?: string;
  source?: string;
  content?: any;
  isAutoplay?: boolean;
  onEnded?: () => void;
};

const PlyrInstance = ({
  poster,
  source,
  content = {},
  isAutoplay = false,
  onEnded,
}: PlyrProps) => {
  const playerRef = useRef(null);
  const hls = useRef(new Hls());
  const [session] = useSession();
  const { locale } = useRouter();
  let plyrRef = null;
  let totalSecondWatched = 0;
  let interval = null;

  const videoOptions: Options = {
    autoplay: isAutoplay,
    controls: [
      'play-large',
      'play',
      'rewind',
      'fast-forward',
      'progress',
      'current-time',
      'mute',
      'volume',
      'captions',
      'settings',
      'pip',
      'airplay',
      'fullscreen',
    ],
  };

  const sendTrackingEvent = (totalPlayedSeconds) => {
    const totalPlayedHours = totalPlayedSeconds / 60;
    // @ts-ignore
    const userId = session ? session.user?.id : '';
    const params = {
      userId,
      content,
      language: convertLanguageCodeToName(locale),
      playedHour: totalPlayedHours,
    };
    sendVideoWatchDepth(params);
  };

  hls.current.on(Hls.Events.MANIFEST_LOADED, () => {
    const player = new Plyr('#plyr', videoOptions);
    plyrRef = player;
    if (isAutoplay) {
      player.muted = true;
      player.play();
    }
    player.on('playing', () => {
      interval = setInterval(() => {
        totalSecondWatched += 1;
      }, 1000);
    });
    player.on('pause', () => {
      console.log(
        `PAUSED - Total user has watched in second: ${totalSecondWatched}`,
      );
      sendTrackingEvent(totalSecondWatched);
      clearInterval(interval);
      totalSecondWatched = 0;
    });
    player.on('ended', () => {
      console.log(
        `ENDED - Total user has watched in second: ${totalSecondWatched}`,
      );
      sendTrackingEvent(totalSecondWatched);
      clearInterval(interval);
      totalSecondWatched = 0;
      onEnded?.();
    });
  });

  useEffect(() => {
    if (!playerRef.current) return;
    hls.current.loadSource(source);
    hls.current.attachMedia(playerRef.current);
  }, [videoOptions]);

  useEffect(() => {
    return () => {
      if (plyrRef) {
        console.log(
          `ENDED - Total user has watched in second: ${totalSecondWatched}`,
        );
        sendTrackingEvent(totalSecondWatched);
        clearInterval(interval);
        totalSecondWatched = 0;
        hls.current.destroy();
        hls.current.detachMedia();
        plyrRef.destroy();
      }
    };
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      autoPlay={isAutoplay}
      muted={isAutoplay}
      controls
      playsInline
      id="plyr"
      poster={poster || '/static/img/empty_detail.png'}
      ref={playerRef}
    />
  );
};

type Props = {
  poster?: string;
  source: string;
  content?: any;
  isAutoplay?: boolean;
  onEnded?: () => void;
};

const VideoPlayer = ({
  poster,
  source,
  content,
  isAutoplay = false,
  onEnded,
}: Props) => {
  const supported = Hls.isSupported();

  return (
    <div className="wrapper">
      {supported ? (
        <PlyrInstance
          poster={poster}
          source={source}
          content={content}
          isAutoplay={isAutoplay}
          onEnded={onEnded}
        />
      ) : (
        <video
          id="plyr"
          src={source}
          controls
          playsInline
          autoPlay={isAutoplay}
          muted={isAutoplay}
          poster={poster || '/static/img/empty_detail.png'}
          onEnded={onEnded}
        />
      )}
    </div>
  );
};

export default memo(VideoPlayer);
